import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api, tradeAuth, toastApiError } from "@/lib/api";

const Field = ({ label, value, onChange, suffix, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.25em] text-gold/70">{label}</span>
    <div className="mt-1 flex items-center rounded-full border border-gold/30 bg-black/50 px-4">
      <input type="number" min="0" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="off"
        className="w-full bg-transparent py-2.5 font-mono-naevo text-white outline-none" data-testid={testid} />
      <span className="text-xs text-white/40">{suffix}</span>
    </div>
  </label>
);

export const LimitsPanel = () => {
  const auth = tradeAuth();
  const [form, setForm] = useState({ loss: "", target: "", minutes: "" });
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!auth) return;
    api.get("/player/limits", { params: { session_id: auth.session_id, account_id: auth.account_id } }).then((r) => {
      setData(r.data);
      setForm({ loss: r.data.daily_loss_limit ?? "", target: r.data.daily_profit_target ?? "", minutes: r.data.session_minutes ?? "" });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.account_id]);

  if (!auth) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/player/limits", {
        session_id: auth.session_id, account_id: auth.account_id,
        daily_loss_limit: Number(form.loss) || null, daily_profit_target: Number(form.target) || null, session_minutes: parseInt(form.minutes, 10) || null,
      });
      setData(r.data);
      localStorage.setItem("naevo_session_minutes", String(r.data.session_minutes || ""));
      toast.success("Limits saved");
    } catch (e) { toastApiError(toast, e, "Could not save limits"); } finally { setSaving(false); }
  };
  const pnl = data?.today?.pnl ?? 0;

  return (
    <section className="casino-frame rounded-2xl p-5 sm:p-6" data-testid="limits-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" /><h3 className="font-casino text-xl text-gold-grad">Daily limits</h3></div>
        <div className="font-mono-naevo text-xs text-white/60" data-testid="limits-today">
          Today <span className={pnl >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}>{pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}</span> · {data?.today?.trades ?? 0} rounds
        </div>
      </div>
      {data?.locked && <div className="mt-3 rounded-lg border border-[#C8102E]/60 bg-[#C8102E]/15 px-3 py-2 text-xs text-[#FFB3BE]" data-testid="limits-locked">{data.locked}</div>}
      {data?.target_hit && <div className="mt-3 rounded-lg border border-[color:var(--naevo-success)]/50 bg-[color:var(--naevo-success)]/10 px-3 py-2 text-xs text-[color:var(--naevo-success)]" data-testid="limits-target-hit">Profit target hit — auto-play and Autopilot pause for today.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <Field label="Daily loss limit" suffix="USD" value={form.loss} onChange={(v) => setForm({ ...form, loss: v })} testid="limit-loss-input" />
        <Field label="Daily profit target" suffix="USD" value={form.target} onChange={(v) => setForm({ ...form, target: v })} testid="limit-target-input" />
        <Field label="Session time limit" suffix="min" value={form.minutes} onChange={(v) => setForm({ ...form, minutes: v })} testid="limit-minutes-input" />
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-white/45">Applies to the {auth.type === "demo" ? "Demo" : "Real"} account {auth.account_id}. Resets at 00:00 UTC.</p>
        <button onClick={save} disabled={saving} className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2" data-testid="limits-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save limits
        </button>
      </div>
    </section>
  );
};
